import React, { createContext, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, ApiError } from './api';

export interface CurrentUser {
  id: number;
  username: string;
  role: 'admin' | 'super';
  tenant_id: number;
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = 'qn_current_user';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as CurrentUser; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // 简单的探活：401 时清掉本地
  useEffect(() => {
    if (!user) return;
    api.get('/api/accounts').catch((err) => {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    });
  }, []);

  async function login(username: string, password: string) {
    setLoading(true);
    try {
      const data = await api.post<{ user: CurrentUser }>('/api/auth/login', { username, password });
      setUser(data.user);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data.user));
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    await api.post('/api/auth/logout');
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}
